import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ImagePlus,
  Trash2,
  Upload,
} from "lucide-react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { api, ApiError } from "@/lib/api";
import { formatFileSize } from "@/lib/format";
import { cn } from "@/lib/utils";

const ACCEPTED = "image/png,image/jpeg,image/webp,image/gif";
const MAX_IMAGES = 10;
const MAX_SIZE = 8 * 1024 * 1024;

export function ReferenceImagesPanel({
  slug,
  onToast,
}: {
  slug: string;
  onToast?: (kind: "success" | "error" | "info", message: string) => void;
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const list = useQuery({
    queryKey: ["reference-images", slug],
    queryFn: () => api.listReferenceImages(slug),
  });

  const upload = useMutation({
    mutationFn: (files: File[]) => api.uploadReferenceImages(slug, files),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["reference-images", slug] });
      qc.invalidateQueries({ queryKey: ["tournament", slug] });
      onToast?.(
        "success",
        `${res.uploaded.length} immagine${res.uploaded.length === 1 ? "" : ""} caricata${res.uploaded.length === 1 ? "" : "e"}`,
      );
    },
    onError: (e: ApiError | Error) => {
      const msg =
        (e as ApiError)?.body &&
        typeof (e as ApiError).body === "object" &&
        (((e as ApiError).body as { detail?: string })?.detail ??
          (e as Error).message);
      onToast?.("error", `Upload fallito: ${msg ?? String(e)}`);
    },
  });

  const del = useMutation({
    mutationFn: (filename: string) => api.deleteReferenceImage(slug, filename),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["reference-images", slug] });
      qc.invalidateQueries({ queryKey: ["tournament", slug] });
      onToast?.("success", `${res.deleted} eliminata`);
    },
    onError: (e) =>
      onToast?.("error", `Eliminazione fallita: ${String(e)}`),
  });

  const images = list.data?.images ?? [];
  const canUploadMore = images.length < MAX_IMAGES;

  const handleFiles = (fl: FileList | null | File[]) => {
    if (!fl) return;
    const files = Array.from(fl as FileList);
    if (files.length === 0) return;

    // Client-side validation
    const errors: string[] = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) {
        errors.push(`${f.name}: non è un'immagine`);
      } else if (f.size > MAX_SIZE) {
        errors.push(
          `${f.name}: troppo grande (${formatFileSize(f.size)} > ${formatFileSize(MAX_SIZE)})`,
        );
      }
    }
    if (images.length + files.length > MAX_IMAGES) {
      errors.push(
        `Max ${MAX_IMAGES} immagini totali. Rimuovine qualcuna prima.`,
      );
    }
    if (errors.length > 0) {
      onToast?.("error", errors.join(" · "));
      return;
    }
    upload.mutate(files);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ImagePlus className="h-4 w-4" />
            Immagini di riferimento (NB2)
          </CardTitle>
          <span className="text-xs text-slate-500">
            {images.length}/{MAX_IMAGES}
          </span>
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-slate-600">
          Carica immagini che guideranno lo stile della locandina generata da
          Nano Banana (composizione, palette, mood). Verranno aggiunte come
          <code className="mx-1 rounded bg-slate-100 px-1 font-mono text-[11px]">
            --input-image
          </code>
          al prompt NB2, insieme ai loghi brand.
        </p>

        {/* Drop zone */}
        {canUploadMore ? (
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              handleFiles(e.dataTransfer.files);
            }}
            className={cn(
              "flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors cursor-pointer",
              dragging
                ? "border-moving-500 bg-moving-50"
                : "border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100",
            )}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-6 w-6 text-slate-400" />
            <div className="text-sm font-medium text-slate-700">
              Trascina qui le immagini o clicca per selezionare
            </div>
            <div className="text-[11px] text-slate-500">
              PNG, JPG, WEBP, GIF · max {formatFileSize(MAX_SIZE)} per file
            </div>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED}
              multiple
              hidden
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            Raggiunto il limite di {MAX_IMAGES} immagini. Rimuovine qualcuna per
            caricarne di nuove.
          </div>
        )}

        {upload.isPending ? (
          <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
            Upload in corso…
          </div>
        ) : null}

        {/* Grid immagini */}
        {list.isLoading ? (
          <div className="text-sm text-slate-500">Caricamento…</div>
        ) : images.length === 0 ? (
          <div className="py-2 text-center text-xs text-slate-500">
            Nessuna immagine di riferimento caricata.
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
            {images.map((img) => (
              <div
                key={img.filename}
                className="group relative overflow-hidden rounded-md border border-slate-200 bg-slate-100"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={img.url}
                    alt={img.filename}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
                <button
                  onClick={() => {
                    if (
                      confirm(`Eliminare "${img.filename}"?`)
                    ) {
                      del.mutate(img.filename);
                    }
                  }}
                  disabled={del.isPending}
                  className="absolute right-1 top-1 rounded bg-black/60 p-1 text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100"
                  aria-label={`Elimina ${img.filename}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
                <div className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100">
                  {img.filename}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

